<template>
    <LaBaseTable data-test="alert-config-table" :headers="headers" :items="data" stripped>
        <template #item-code="{ item }">
            <div>
                <div class="code">{{ item.code }}</div>
                <div class="description">{{ item.description }}</div>
            </div>
        </template>

        <template #item-enabled="{ item }">
            <LaStatusLabel
                :type="item.enabled ? 'success' : 'warning'"
                :label="item.enabled ? 'enabled' : 'disabled'"
            />
        </template>

        <template #item-channel="{ item }">
            <span v-if="item.channel">{{ item.channel }}</span>
            <span v-else class="muted" data-test="alert-default-channel">
                default{{ defaultChannel ? ` · ${defaultChannel}` : "" }}
            </span>
        </template>

        <template #item-aggregation_seconds="{ item }">
            <div>
                <div>{{ formatWindow(item.aggregation_seconds) }}</div>
                <div v-for="(field, key) in item.settings_schema" :key="key" class="muted">
                    {{ field.label }}: {{ formatSetting(field, item.settings?.[key]) }}
                </div>
            </div>
        </template>

        <template #item-title_template="{ item }">
            <div>
                <code class="template template-title">{{ item.title_template }}</code>
                <code class="template">{{ item.item_template }}</code>
            </div>
        </template>

        <template #item-stats="{ item }">
            <div>
                <div>{{ formatCounts(item.stats.counts) || "—" }}</div>
                <div v-if="item.stats.last_sent_at" class="muted">
                    last sent {{ formatDateTimePragueTimeZone(item.stats.last_sent_at) }}
                </div>
            </div>
        </template>

        <template #item-actions="{ item }">
            <LaActionIcon type="edit" title="Edit" data-test="edit-alert-config" @click="edit(item)" />
            <LaActionIcon type="test" title="Send a test alert" data-test="test-alert-config" @click="test(item)" />
        </template>
    </LaBaseTable>
</template>

<script>
import LaBaseTable from "@/components/BaseTable";
import LaStatusLabel from "@/components/StatusLabel";
import LaActionIcon from "@/components/button/ActionIcon";
import store from "@/store/store";
import { mutationTypes as dialogMutationTypes } from "@/store/Dialog/types";
import { formatDateTimePragueTimeZone } from "@/helpers/filters.js";
import { formatCounts, formatSetting, formatWindow } from "@/components/alerts/helpers";

export default {
    name: "LaAlertConfigTable",
    components: {
        LaBaseTable,
        LaStatusLabel,
        LaActionIcon,
    },
    props: {
        data: {
            required: true,
        },
        statsDays: {
            type: Number,
            default: 7,
        },
        defaultChannel: {
            type: String,
            default: null,
        },
    },
    computed: {
        headers() {
            return [
                { text: "type", value: "code", flex: "2" },
                { text: "status", value: "enabled" },
                { text: "channel", value: "channel" },
                { text: "aggregation / settings", value: "aggregation_seconds", flex: "1.5" },
                { text: "format", value: "title_template", flex: "2", sortable: false },
                { text: `last ${this.statsDays} days`, value: "stats", sortable: false },
                { text: "", value: "actions", sortable: false },
            ];
        },
    },
    setup() {
        const edit = (item) => {
            store.commit(dialogMutationTypes.openDialog, {
                dialogName: "alertConfigForm",
                dialogHeading: `Edit ${item.code}`,
                item,
            });
        };

        const test = (item) => {
            store.commit(dialogMutationTypes.openDialog, {
                dialogName: "alertTest",
                dialogHeading: `Send a test of ${item.code}`,
                item,
            });
        };

        return { edit, test, formatCounts, formatSetting, formatWindow, formatDateTimePragueTimeZone };
    },
};
</script>

<style lang="scss" scoped>
.code {
    font-weight: 600;
}
.description,
.muted {
    font-size: 0.8rem;
    opacity: 0.7;
}
.template {
    display: block;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.8rem;
    background: none;
    padding: 0;
    &-title {
        font-weight: 600;
    }
}
</style>
